import { useCallback, useId, useState } from 'react';
import type * as React from 'react';
import { useCreate, useDelete, useUpdate } from '@refinedev/core';
import { Plus, Trash2 } from 'lucide-react';
import { Page, Notice } from '@/components/page';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { SortableList } from '@/components/sortable-list';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogForm,
  DialogBody,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { FormField, Input } from '@/components/ui/field';
import { IconField } from '@/components/icon-field';
import { Badge, Switch, Tooltip } from '@/components/ui/primitives';
import { RESOURCES, type AdminInterest } from '@/api/resources';
import { isInUse, useAdminList, useReorder } from '@/hooks/use-admin-resource';
import { ApiError } from '@/api/errors';
import { notify } from '@/lib/notify';
import { formatNumber } from '@/lib/format';

/**
 * Interests (06 §2).
 *
 * ⚠️ An interest that children have already chosen is never deleted. The API
 * answers `409 OPTION_IN_USE`, and `usageCount` is on the read DTO precisely so
 * the panel can offer "deactivate" *before* the admin clicks rather than
 * explaining a refusal afterwards — deactivating removes it from new wizard
 * definitions while every child who already picked it keeps it.
 */
export function InterestsPage(): React.JSX.Element {
  const { rows, isLoading, error, refetch, invalidate } = useAdminList<AdminInterest>(
    RESOURCES.interests,
  );
  const reorder = useReorder(RESOURCES.interests, `${RESOURCES.interests}/reorder`);

  const [editing, setEditing] = useState<AdminInterest | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminInterest | null>(null);

  const { mutateAsync: update } = useUpdate();
  const { mutateAsync: remove } = useDelete();

  const toggleActive = useCallback(
    async (interest: AdminInterest) => {
      try {
        await update({
          resource: RESOURCES.interests,
          id: interest.id,
          values: { isActive: !interest.isActive },
          successNotification: false,
          errorNotification: false,
        });
        notify.success(interest.isActive ? 'Deactivated' : 'Activated', interest.name);
      } catch (err) {
        notify.error(err, 'The change was not saved');
      } finally {
        invalidate();
      }
    },
    [update, invalidate],
  );

  return (
    <Page
      title="Interests"
      description="What a child can pick in the wizard. Order here is the order the app shows them in."
      breadcrumbs={[{ label: 'Master data' }, { label: 'Interests' }]}
      action={
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Add interest
        </Button>
      }
    >
      <div className="space-y-5">
        <Notice title="In use means deactivate, not delete">
          An interest children have already chosen cannot be deleted — deactivating removes it from
          new profiles while everyone who picked it keeps it.
        </Notice>

        <div className="overflow-hidden rounded-card border border-line bg-surface">
          {error ? (
            <ErrorState error={error} onRetry={refetch} endpoint="GET /admin/interests" />
          ) : isLoading ? (
            <TableSkeleton columns={4} />
          ) : !rows || rows.length === 0 ? (
            <EmptyState
              title="No interests yet"
              description="Add the first interest and it becomes selectable in the next wizard draft."
              action={
                <Button variant="primary" onClick={() => setEditing('new')}>
                  <Plus aria-hidden="true" />
                  Add interest
                </Button>
              }
            />
          ) : (
            <SortableList
              items={rows}
              itemKey={(interest) => interest.id}
              onReorder={(ids) => void reorder(ids)}
              renderItem={(interest) => (
                <div className="flex flex-wrap items-center gap-3">
                  {interest.iconUrl ? (
                    <img
                      src={interest.iconUrl}
                      alt=""
                      width={28}
                      height={28}
                      className="size-7 shrink-0 rounded object-cover"
                    />
                  ) : (
                    <span className="size-7 shrink-0 rounded border border-line bg-surface-sunken" />
                  )}

                  <button
                    type="button"
                    className="min-w-0 flex-1 rounded text-left"
                    onClick={() => setEditing(interest)}
                  >
                    <span className="block truncate text-sm font-medium text-ink">
                      {interest.name}
                    </span>
                  </button>

                  <span className="text-xs text-ink-soft" data-numeric>
                    {formatNumber(interest.usageCount)} children
                  </span>

                  {interest.isActive ? (
                    <Badge tone="success">Active</Badge>
                  ) : (
                    <Badge tone="neutral">Inactive</Badge>
                  )}

                  <Tooltip label={interest.isActive ? 'Deactivate' : 'Activate'}>
                    <span>
                      <Switch
                        checked={interest.isActive}
                        onCheckedChange={() => void toggleActive(interest)}
                        aria-label={`${interest.isActive ? 'Deactivate' : 'Activate'} ${interest.name}`}
                      />
                    </span>
                  </Tooltip>

                  <Tooltip
                    label={
                      interest.usageCount > 0
                        ? 'In use by existing profiles — deactivate instead'
                        : 'Delete'
                    }
                  >
                    <span>
                      <Button
                        size="icon"
                        variant="ghost"
                        disabled={interest.usageCount > 0}
                        aria-label={`Delete ${interest.name}`}
                        onClick={() => setDeleting(interest)}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              )}
            />
          )}
        </div>
      </div>

      {editing ? (
        <InterestDialog
          interest={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={`Delete ${deleting?.name ?? ''}?`}
        description="This removes the interest entirely. It cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove({
              resource: RESOURCES.interests,
              id: deleting.id,
              successNotification: false,
              errorNotification: false,
            });
            notify.success('Deleted', deleting.name);
            setDeleting(null);
          } catch (err) {
            if (isInUse(err)) {
              notify.error(err, 'Still in use');
            } else {
              notify.error(err, 'The interest was not deleted');
            }
          } finally {
            invalidate();
          }
        }}
      />
    </Page>
  );
}

// ---------------------------------------------------------------------------

function InterestDialog({
  interest,
  onClose,
  onSaved,
}: {
  interest: AdminInterest | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(interest?.name ?? '');
  // undefined = untouched. On create there is nothing to leave alone, so a
  // key is required before the form can be submitted.
  const [iconKey, setIconKey] = useState<string | null | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  const nameId = useId();

  const { mutateAsync: create } = useCreate();
  const { mutateAsync: update } = useUpdate();

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setFieldErrors({});

    try {
      if (interest) {
        await update({
          resource: RESOURCES.interests,
          id: interest.id,
          values: { name: name.trim(), ...(iconKey !== undefined ? { iconKey } : {}) },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Saved', name.trim());
      } else {
        await create({
          resource: RESOURCES.interests,
          values: { name: name.trim(), iconKey },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Added', name.trim());
      }
      onSaved();
    } catch (err) {
      // Field-level messages come from `details.fields`, never a JSON dump.
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
      notify.error(err, 'The interest was not saved');
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        title={interest ? 'Edit interest' : 'Add interest'}
        description="Names appear in the app exactly as typed."
      >
        <DialogForm
          onSubmit={(event) => {
            void submit(event);
          }}
          noValidate
        >
          <DialogBody>
            <FormField label="Name" htmlFor={nameId} error={fieldErrors['name']} required>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={60}
                autoFocus
                required
              />
            </FormField>

            <IconField
              onChange={setIconKey}
              initialPreviewUrl={interest?.iconUrl ?? null}
              error={fieldErrors['iconKey']}
              required
            />
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={pending}
              disabled={!name.trim() || (!interest && !iconKey)}
            >
              {interest ? 'Save changes' : 'Add interest'}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
